import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import Skeleton from 'react-loading-skeleton';
import { cloneDeep } from 'lodash';

import { APIgetFiles } from '../../api/Files';

import FiltersFileStatuses from '../Sidebar/FiltersFileStatuses';
import FiltersFileTypes from '../Sidebar/FiltersFileTypes';
import FiltersFileExtensions from '../Sidebar/FiltersFileExtensions';
import FiltersFileVisibility from '../Sidebar/FiltersFileVisibility';
import FiltersFileNoCollections from '../Sidebar/FiltersFileNoCollections';
import FiltersFileUsers from '../Sidebar/FiltersFileUsers';
import FiltersFilePeople from '../Sidebar/FiltersFilePeople';
import FiltersFileDates from '../Sidebar/FiltersFileDates';
import FiltersFileOrientation from '../Sidebar/FiltersFileOrientation';
import FiltersFileTerms from '../Sidebar/FiltersFileTerms';
import FiltersSearch from '../Sidebar/FiltersSearch';
import ErrorBoundary from '../ErrorBoundary';
import ModalSubheader from '../ModalSubheader';

import { userIsAdmin } from '../../helpers/Auth';
import { getDatesFiltersFromSelectedFilters } from '../../helpers/Files';
import { getSetting } from '../../helpers/Settings';
import { prepareSearchFilters } from '../../helpers/URLParams';

import { initialFilesFilters } from '../../reducers/Files';

const FiltersSelectionModal = ({
  filters,
  handleClose,
  handleSaveFilters,
  initialSelectedFilters,
  initialFilters,
  taxonomies,
}) => {
  const intl = useIntl();

  const [openFilters, setOpenFilters] = useState([]);
  const [availableFilters, setAvailableFilters] = useState({});
  const [confirmedFilters, setConfirmedFilters] = useState(initialSelectedFilters);
  const [customFilters, setCustomFilters] = useState(
    initialFilters ? { ...cloneDeep(initialFilesFilters), ...initialFilters } : cloneDeep(filters),
  );
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState(initialSelectedFilters);
  const [search, setSearch] = useState('');

  const prepareNewSelectedFilters = (newSelectedFilters) => newSelectedFilters.map(filter => {
    if (filter.type === 'search') {
      return {
        ...filter,
        key: filter.key.replace(/\\/g, ''),
      };
    }
    return filter;
  });

  const handleGetFiles = useCallback(() => {
    const filesFilters = prepareSearchFilters(selectedFilters);
    const datesFilter = getDatesFiltersFromSelectedFilters(selectedFilters);

    delete filesFilters.dates;
    delete filesFilters.dates_from;
    delete filesFilters.dates_to;
    delete filesFilters.original_dates;
    delete filesFilters.original_dates_from;
    delete filesFilters.original_dates_to;
    delete filesFilters.expiration_dates;
    delete filesFilters.expiration_dates_from;
    delete filesFilters.expiration_dates_to;

    setLoading(true);
    APIgetFiles({
      page: 0,
      per_page: 1,
      ...filesFilters,
      ...datesFilter,
      only_filters: true,
      smart_collection: true,
    }).then(response => {
      if (
        response.data.data.filters.selected_blocked_filters?.length > 0 &&
        response.data.data.filters.selected_filters.length === 0
      ) {
        setTotal(0); // If only blocked filters are selected, matching files will be 0
      } else {
        setTotal(response.data.meta.total);
      }
      setAvailableFilters({ ...cloneDeep(initialFilesFilters), ...response.data.data.filters });

      const selectedFiltersToPrepare = response?.data?.data?.filters?.selected_filters;
      const blockedFiltersToPrepare = response?.data?.data?.filters?.selected_blocked_filters;

      const allFilters = [
        ...(Array.isArray(selectedFiltersToPrepare) ? selectedFiltersToPrepare : []),
        ...(Array.isArray(blockedFiltersToPrepare) ? blockedFiltersToPrepare : []),
      ];

      setConfirmedFilters(prepareNewSelectedFilters(allFilters));
      setLoading(false);
    }).catch(err => {
      if (window.axios.isCancel(err)) {
        return;
      }
      setTotal(null);
      setLoading(false);
    });
  }, [selectedFilters]);

  const filesFilters = getSetting('filters_files');
  const taxonomyFilters = [];

  const resetSelectedFilters = () => {
    setSelectedFilters([]);
  };

  const handleChangeFilters = (changedFilters, reset = false) => {
    if (reset) {
      resetSelectedFilters();
    }

    setCustomFilters({ ...customFilters, ...changedFilters });

    if ('no_collections' in changedFilters) {
      setSelectedFilters(prev => {
        const value = changedFilters.no_collections;

        const filtered = prev.filter(item => item.type !== 'no_collections');

        if (value) {
          return [
            ...filtered,
            {
              type: 'no_collections',
              title: 'No Collections',
              label: 'No Collections',
              key: value,
            },
          ];
        }

        return filtered;
      });
    }
  };

  const handleToggleFilter = (filter) => {
    if (openFilters.includes(filter)) {
      setOpenFilters(openFilters.filter(cf => (cf !== filter)));
    } else {
      setOpenFilters(openFilters.concat([filter]));
    }
  };

  const handleAddFilter = (filter) => {
    setSelectedFilters(prev => [...prev, { ...filter, key: `${filter.key}` }]);
  };

  const handleClearFilters = (filterType) => {
    setSelectedFilters(prevFilters => prevFilters.filter(f => `${f.type}` !== `${filterType}`));

    setCustomFilters(prev => {
      if (filterType === 'dates') {
        return {
          ...prev,
          date: null,
          date_from: null,
          date_to: null,
        };
      } if (filterType === 'original_dates') {
        return {
          ...prev,
          original_date: null,
          original_date_from: null,
          original_date_to: null,
        };
      } if (filterType === 'expiration_dates') {
        return {
          ...prev,
          expiration_date: null,
          expiration_date_from: null,
          expiration_date_to: null,
        };
      } if (filterType === 'orientation') {
        return {
          ...prev,
          orientation: null,
        };
      }
      return {
        ...prev,
        [filterType]: initialFilesFilters[filterType],
      };
    });
  };

  const handleRemoveFilter = (filterType, filterKey) => {
    if (['dates', 'original_dates', 'expiration_dates', 'orientation'].includes(filterType)) {
      handleClearFilters(filterType);
    } else if (window._.isArray(customFilters[filterType])) {
      setSelectedFilters(prev => prev.filter(f => !(`${f.key}` === `${filterKey}` && f.type === filterType)));

      setCustomFilters(prev => ({
        ...prev,
        [filterType]: prev[filterType].filter(f => `${f}` !== `${filterKey}`),
      }));
    }
  };

  const customSetSearch = (searchValue) => {
    setSelectedFilters(prev => prev.filter(f => !(f.type === 'search')));
    handleAddFilter({
      type: 'search',
      key: `${searchValue}`,
      label: intl.formatMessage({ defaultMessage: 'Search: {search}' }, { search: searchValue }),
    });
    setSearch(searchValue);
  };

  const customSetSearchFields = (value, label) => {
    handleAddFilter({
      type: 'search_fields',
      key: `${value}`,
      label,
    });
  };

  const customSetQueryType = (value, label) => {
    if (selectedFilters.find(f => f.type === 'query_type' && f.key === value)) return;

    setSelectedFilters(prev => prev.filter(f => !(f.type === 'query_type')));
    handleAddFilter({
      type: 'query_type',
      key: `${value}`,
      label,
    });
  };

  const customRemoveSearchFields = (type, key) => {
    setSelectedFilters(prev => prev.filter(f => !(`${f.key}` === `${key}` && f.type === type)));
  };

  useEffect(() => {
    const initialSearch = initialSelectedFilters.find(f => f.type === 'search');

    if (initialSearch) {
      setSearch(initialSearch.key);
    }
  }, [initialSelectedFilters]);

  useEffect(() => {
    const renameMap = {
      dates: 'date',
      original_dates: 'original_date',
      expiration_dates: 'expiration_date',
    };

    const updatedFilters = Object.entries(customFilters).reduce((acc, [key, value]) => {
      const newKey = renameMap[key] || key;
      acc[newKey] = value;
      return acc;
    }, {});

    const hasChanges = Object.keys(customFilters).some(key => renameMap[key]);

    if (hasChanges) {
      setCustomFilters(updatedFilters);
    }
  }, [customFilters]);

  useEffect(() => {
    handleGetFiles();
  }, [handleGetFiles]);

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose}>
        {intl.formatMessage({ defaultMessage: 'Select Filters' })}
      </ModalHeader>
      <ModalSubheader>
        <h5 className="mb-3">
          {loading && <Skeleton style={{ width: '100%' }} />}
          {(!loading && total !== null) && intl.formatMessage({ defaultMessage: '{total} available files' }, { total })}
          {(!loading && total === null) && intl.formatMessage({ defaultMessage: 'Unable to load available files' })}
        </h5>
      </ModalSubheader>
      <ModalBody>
        <div className="files-filters files-filters-modal">
          <FiltersSearch
            type="files"
            customSearch={search}
            customSetSearch={customSetSearch}
            customSetSearchFields={customSetSearchFields}
            customRemoveSearchFields={customRemoveSearchFields}
            customSelectedSearchFields={confirmedFilters.filter(f => f.type === 'search_fields').map(f => f.key)}
            customSetQueryType={customSetQueryType}
            customSearchQueryType={confirmedFilters.find(f => f.type === 'query_type')}
            prefix="modal-"
            showQueryType={true}
          />
          {Object.keys(availableFilters).length > 0 && filesFilters.map((filter) => {
            if (!filter.visible || (filter.admins_only && !userIsAdmin())) {
              if (filter.key && filter.key.indexOf('tax_') === 0) {
                taxonomyFilters.push(parseInt(filter.key.replace('tax_', '')));
              }
              return false;
            }

            if (filter.key === 'f_status') {
              return (
                <FiltersFileStatuses
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.statuses}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customClearSelectedFilters={handleClearFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key === 'f_type') {
              return (
                <FiltersFileTypes
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.types}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customClearSelectedFilters={handleClearFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key === 'f_extension') {
              return (
                <FiltersFileExtensions
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.extensions}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customClearSelectedFilters={handleClearFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key === 'f_visibility') {
              return (
                <FiltersFileVisibility
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.visibility}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customClearSelectedFilters={handleClearFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key === 'f_no_collections') {
              return (
                <FiltersFileNoCollections
                  key={filter.key}
                  handleChangeFilters={handleChangeFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key === 'f_owner') {
              return (
                <FiltersFileUsers
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customFilters={customFilters}
                />
              );
            }

            if (filter.key === 'f_people' && getSetting('face_recognition')) {
              return (
                <FiltersFilePeople
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customFilters={customFilters}
                />
              );
            }

            if (filter.key === 'f_date') {
              return (
                <FiltersFileDates
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customClearSelectedFilters={handleClearFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key === 'f_original_date') {
              return (
                <FiltersFileDates
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  dateMode="original"
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customClearSelectedFilters={handleClearFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key === 'f_expiration_date') {
              return (
                <FiltersFileDates
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  dateMode="expiration"
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customClearSelectedFilters={handleClearFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key === 'f_orientation') {
              return (
                <FiltersFileOrientation
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.orientation}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(filter.key); }}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customClearSelectedFilters={handleClearFilters}
                  customFilters={customFilters}
                  prefix="modal-"
                />
              );
            }

            if (filter.key.indexOf('tax_') === 0) {
              const taxonomyId = parseInt(filter.key.replace('tax_', ''));
              const taxonomy = taxonomies.find(t => (t.id === taxonomyId));
              if (!taxonomy) {
                return null;
              }
              taxonomyFilters.push(taxonomy.id);
              return (
                <FiltersFileTerms
                  key={`tax_${taxonomy.id}`}
                  isOpen={openFilters.includes(`tax_${taxonomy.id}`)}
                  taxonomy={taxonomy}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(`tax_${taxonomy.id}`); }}
                  customFilters={customFilters}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customAvailableFilters={availableFilters}
                  prefix="modal-files-filter-"
                />
              );
            }

            return null;
          })}

          {taxonomies.map((tax) => {
            if (!taxonomyFilters.includes(tax.id)) {
              return (
                <FiltersFileTerms
                  key={`tax_${tax.id}`}
                  isOpen={openFilters.includes(`tax_${tax.id}`)}
                  taxonomy={tax}
                  handleChangeFilters={handleChangeFilters}
                  handleToggleFilter={() => { handleToggleFilter(`tax_${tax.id}`); }}
                  customFilters={customFilters}
                  customAddFilter={handleAddFilter}
                  customRemoveFilter={handleRemoveFilter}
                  customAvailableFilters={availableFilters}
                  prefix="modal-"
                />
              );
            }
            return null;
          })}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          onClick={() => {
            handleClose();
          }}
        >
          {intl.formatMessage({ defaultMessage: 'Cancel' })}
        </Button>
        <Button
          color="primary"
          onClick={() => {
            handleSaveFilters(confirmedFilters);
            handleClose();
          }}
        >
          {intl.formatMessage({ defaultMessage: 'Save' })}
        </Button>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  filters: state.Files.filters,
  taxonomies: state.Metadata.taxonomies,
});

FiltersSelectionModal.propTypes = {
  filters: PropTypes.object.isRequired,
  initialFilters: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  handleSaveFilters: PropTypes.func.isRequired,
  initialSelectedFilters: PropTypes.array.isRequired,
  taxonomies: PropTypes.array.isRequired,
};

FiltersSelectionModal.defaultProps = {
  initialFilters: null,
};

export default connect(mapStateToProps)(FiltersSelectionModal);
